import { gapi } from 'gapi-script';
import { getFSmessage } from './messages.js';

/* global google */

var tokenClient = null; // Initialize tokenClient to null
var storedSettings = {};
var allLabels = [];

// firmId and userId are used to check whether a particular email has already been processed and meta data stored in firestore
var firmId = null;
var userId = null;

async function initializeApi(settings, firm_id=null, user_id=null) {
  firmId = firm_id;
  userId = user_id;
  settings.scope ||= "https://www.googleapis.com/auth/gmail.readonly"
  console.log("Initializing Gmail API with settings:", settings);
  storedSettings = settings;
  await new Promise((resolve, reject) => {
    gapi.load('client', async () => {
      try {
        await gapi.client.init({
          discoveryDocs: ['https://www.googleapis.com/discovery/v1/apis/gmail/v1/rest'],
        });
        console.log("Gmail API initialized successfully.");
        resolve();
      } catch (error) {
        console.error('Error initializing API:', error);
        reject();
      }
    });
  });
  console.log('gapi.client access token in initialize: ' + JSON.stringify(gapi.client.getToken()));
  tokenClient = google.accounts.oauth2.initTokenClient({
    client_id: location.hostname == "app.patensys.com" ? '14976022517-ecbu1r5kcook8fa8jut4m4tqiquanb14.apps.googleusercontent.com' : '383963345176-lcesf9qa5phktfqbtfiivv6d980ddbka.apps.googleusercontent.com',
    scope: settings.scope,
    prompt: '',
    hint: settings.emailAddress,
    callback: ''
  });
}

function authenticate() {
  if (alreadySignedIn()) {
    return Promise.resolve();
  }

  //TODO raise unless tokenClient
  return new Promise((resolve, reject) => {
    tokenClient.callback = (response) => {
      if (response.error !== undefined) {
        console.log("error");
        console.log(response);
        reject(response);
      }
      console.log('gapi.client access token in callback: ' + JSON.stringify(gapi.client.getToken()));
      console.log(response);
      resolve(response.accessToken);
    };
    tokenClient.requestAccessToken({prompt: '', hint: storedSettings.emailAddress});
  });
}

function signout() {
  google.accounts.oauth2.revoke(gapi.client.getToken().access_token, () => {
    console.log("User signed out and token revoked.");
    gapi.client.setToken(null); // Clear stored token
  });
}

function alreadySignedIn() {
  if (!tokenClient) {
    return false;
  }
  if (!gapi.client) {
    return false;
  }
  return gapi.client.getToken() !== null;
}

async function getProfileEmailAddress() {
  const response = await gapi.client.gmail.users.getProfile({userId: 'me'});
  if (response && response.result) {
    return response.result.emailAddress;
  } else {
    return null;
  }
}

// the getEmails function is mostly called to get emails for processing
async function getEmails(filter, maxResults=100, pageToken=null) {
  let nextPageToken = null;
  try {
    let arg = {
      userId: 'me',
      q: filter, // Filter emails by subject or any query
      maxResults: maxResults, // Maximum allowed per request (default is 100)
    };
    if (pageToken) {
      arg.pageToken = pageToken;
    }

    // the list method only return emailIds and threadIds
    const response = await retryWithExponentialBackoff(() => gapi.client.gmail.users.messages.list(arg));
    const messages = response.result.messages || [];

    let filteredMessages = [];
    // for each emailId, check whether it is already in firestore
    for (const message of messages) {
      //check if email is already in firestore, if not then add to be retrieved
      if (!await getFSmessage(firmId, userId, message.id)) {
        filteredMessages.push(message);
      }
    }

    // Check for the next page token
    nextPageToken = response.result.nextPageToken;

    let detailedEmails = await retryWithExponentialBackoff(() => fetchEmailsInBatch(filteredMessages));

    return {query: filter, nextPageToken: nextPageToken, messages: detailedEmails};

  } catch (error) {
    console.error('Error fetching emails:', error);
    throw error; // Propagate the error to the caller
  }
}

// the getThread function is mostly called to get emails for display
async function getThread(threadId) {
  if (!threadId) {
    return null;
  }

  try {
    const response =  await retryWithExponentialBackoff(() => gapi.client.gmail.users.threads.get({
      userId: 'me',
      id: threadId,
      format: 'full', // Use 'full' to get the complete thread
    }));
    return response.result;
  } catch (error) {
    console.error('Error in getThread request:', error);
    // if error is 404, then the thread is not found
    if (error.result && error.result.error && error.result.error.code === 404) {
      console.log('Thread not found:', threadId);
      throw new Error('Thread not found');
    }
  }
}

function getThreadSender(thread) {
  if (!thread || !thread.messages || (thread.messages.length == 0))
    return "";

  for (const header of thread.messages[thread.messages.length -1].payload.headers) {
    if (header['name'] === 'From') {
      return header['value']
    }
  }
  return "";
}

function getThreadDate(thread) {
  if (!thread || !thread.messages || (thread.messages.length == 0))
    return "";

  for (const header of thread.messages[thread.messages.length -1].payload.headers) {
    if (header['name'] === 'Date') {
      return header['value']
    }
  }
  return ""; // Return empty string if no date found
}

function getThreadSnippet(thread) {
  if (!thread || !thread.messages || (thread.messages.length == 0))
    return "";

  return thread.messages[thread.messages.length -1].snippet
}

function getThreadSubject(thread) {
  if (!thread || !thread.messages || (thread.messages.length == 0))
    return "";

  for (const header of thread.messages[thread.messages.length -1].payload.headers) {
    if (header['name'] === 'Subject') {
      return header['value']
    }
  }
  return ""; // Return empty string if no subject found
}

async function getEmail(messageId) {
  if (!messageId) {
    return null
  }

  try {
    const response =  await retryWithExponentialBackoff(() => gapi.client.gmail.users.messages.get({
      userId: 'me',
      id: messageId,
    }));
    return response.result;
  } catch (error) {
    console.error('Error in getEmail request:', error);
    throw error;
  }

}

async function getMessages(filter, maxResults=100, pageToken=null) {
  let nextPageToken = null;
  try {
    let arg = {
      userId: 'me',
      q: filter, // Filter emails by subject or any query
      maxResults: maxResults, // Maximum allowed per request (default is 100)
    };
    if (pageToken) {
      arg.pageToken = pageToken;
    }
    const response = await retryWithExponentialBackoff(() => gapi.client.gmail.users.messages.list(arg));
    const messages = response.result.messages || [];

    // Check for the next page token
    nextPageToken = response.result.nextPageToken;
    return {query: filter, nextPageToken: nextPageToken, messages: messages};

  } catch (error) {
    console.error('Error fetching emails:', error);
    throw error; // Propagate the error to the caller
  }
}

function getBody(email) {
  //this might need to be platform specific -> move to service
  if (!email.payload) {
    return '';
  }
  return extractGmailBody(email.payload);
}

function extractGmailBody(payload) {
  let result = {
    html: null,
    plain: null,
  };

  function traverse(part) {
    if (!part) return;

    const mimeType = part.mimeType;
    const body = part.body || {};

    if (mimeType === 'text/html' && body.data && !result.html) {
      result.html = decodeBase64(body.data);
    } else if (mimeType === 'text/plain' && body.data && !result.plain) {
      result.plain = decodeBase64(body.data);
      if (result.plain) {
        result.plain = result.plain.replace(/\n/g, '<br>');
      }
    } else if (mimeType?.startsWith('multipart/') && part.parts?.length) {
      for (const subPart of part.parts) {
        traverse(subPart);
      }
    }
  }

  traverse(payload);

  return result.html || result.plain || '';
}

function decodeBase64(encodedString) {
  // Decode a Base64-encoded string
  if (!encodedString) {
    return "";
  }
  try {
    return decodeURIComponent(
      atob(encodedString.replace(/-/g, "+").replace(/_/g, "/"))
        .split("")
        .map(c => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
  } catch (e) {
    console.error("Failed to decode Base64 string:", e);
    return "Error decoding content.";
  }
}

function getHeaders(email) {
  let headers = {};
  for (const header of email.payload.headers) {
    headers[header["name"]] = header["value"];
  }
  return headers;
}

function getSubjectLine(email) {
  if (!email || !email.payload || !email.payload.headers) {
    return '';
  }
  const subject = email.payload.headers.find(header => (header.name && header.name.toLowerCase() === 'subject'));
  if (subject) {
    return subject.value;
  }
  return '';
}

function getEmailAddrs(email) {
  if (!email || !email.payload || !email.payload.headers) {
    return '';
  }
  let addrs = "";
  for (const header of email.payload.headers) {
    if (header.name.toLowerCase() === 'from' || header.name.toLowerCase() === 'to' || header.name.toLowerCase() === 'cc' || header.name.toLowerCase() === 'bcc') {
      addrs += header.value + " ";
    }
  }
  return addrs;
}

async function fetchEmailsInBatch(messages) {
  if (!messages || messages.length === 0) {
    console.error('No messages to fetch');
    return [];
  }
  const batch = gapi.client.newBatch();

  messages.forEach(message => {
    batch.add(
      gapi.client.gmail.users.messages.get({
        userId: 'me',
        id: message.id,
      })
    );
  });

  try {
    const response = await batch;
    return Object.values(response.result).map(res => res.result);
  } catch (error) {
    console.error('Error in batch request:', error);
    throw error;
  }
}
//maybe we don't even need to worry much about labelling email at this point.
async function labelEmail(label, messageId) {
  console.log("attempting label " + label + " to message " + messageId);
  if (!label || !messageId) {
    console.error('Label or messageId is missing');
    return;
  }
  // Check if the label already exists
  // If it doesn't exist, create it
  // If it exists, use the existing label
  const labelObj = await findOrCreateLabel(label);

  retryWithExponentialBackoff(() => gapi.client.gmail.users.messages.modify({
    userId: 'me',
    id: messageId,
    resource: {
      addLabelIds: [labelObj['id']], // Label(s) to add
      removeLabelIds: [] // Label(s) to remove (optional)
    }
  })).then(response => {
    console.log('Email labeled successfully:', response.result);
  }).catch(error => {
    console.error('Error labeling email:', error);
  });
}

async function unLabelEmail(label, messageId) {
  const labelObj = await findOrCreateLabel(label);

  retryWithExponentialBackoff(() => gapi.client.gmail.users.messages.modify({
    userId: 'me',
    id: messageId,
    resource: {
      addLabelIds: [], // Label(s) to add
      removeLabelIds: [labelObj['id']] // Label(s) to remove (optional)
    }
  })).then(response => {
    console.log('Email unlabeled successfully:', response.result);
  }).catch(error => {
    console.error('Error unlabeling email:', error);
  });

}

async function listLabels() {
  if (!alreadySignedIn() || !gapi.client || !gapi.client.gmail) {
    console.error('User is not signed in or gmail client is not initialized');
    return [];
  }
  if (!allLabels || allLabels.length === 0) {
    try {
      const labelsResponse = await retryWithExponentialBackoff(() => gapi.client.gmail.users.labels.list({
        userId: 'me',
      }));
      allLabels = labelsResponse.result.labels || [];
      return allLabels;
    }
    catch (error) {
      console.error('Error fetching labels:', error);
      return [];
    }
  }
  return allLabels;

}

async function findOrCreateLabel(labelName) {
  try {
    if (!allLabels || allLabels.length === 0) {
      const labelsResponse = await retryWithExponentialBackoff(() => gapi.client.gmail.users.labels.list({
        userId: 'me',
      }));
      allLabels = labelsResponse.result.labels || [];
    }
    var existingLabel = allLabels.find(l => l.name === labelName);
    if (existingLabel) {
      console.log(`Label "${labelName}" already exists with ID: ${existingLabel.id}`);
      return existingLabel;
    } else {
      const createResponse = await retryWithExponentialBackoff(() => gapi.client.gmail.users.labels.create({
        userId: 'me',
        resource: {
          name: labelName,
          labelListVisibility: 'labelHide',
          messageListVisibility: 'hide',
        }
      }));
      console.log('Label created successfully:', createResponse.result);
      allLabels.push(createResponse.result);
      return createResponse.result;
    }
  } catch (error) {
    console.error('Error creating label labels:', error);
    throw(error);
  }
}

async function loadBatch(lastBatch, query='') {
  let token = lastBatch ? lastBatch.nextPageToken : null;
  return await getEmails(query, 25, token);
}

function moreMessages(batch) {
  return batch.nextPageToken;
}

function getOldestTimeQuery(settings) {
  if (settings.oldestProcessedTimestamp) {
    return `before:${settings.oldestProcessedTimestamp}`;
  }
  else {
    return '';
  }
}

function getNewestTimeQuery(settings) {
  if (settings['newestProcessedTimestamp']) {
    return `after:${settings.newestProcessedTimestamp}`;
  }
  else {
    return '';
  }
}

function platformDate(email) {
  if (!email || !email.internalDate) {
    console.log(email);
    return '';
  }
  return email.internalDate;
}

async function retryWithExponentialBackoff(fn, retries = 5, delay = 1000) {
  let attempt = 0;

  while (attempt < retries) {
    try {
      // Attempt to execute the function
      return await fn();
    } catch (error) {
      attempt++;
      if (attempt >= retries) {
        // If all retries are exhausted, rethrow the error
        throw error;
      }

      // Calculate the exponential back-off delay
      const backoffDelay = delay * Math.pow(2, attempt - 1);
      console.log(`Retrying in ${backoffDelay}ms... (Attempt ${attempt} of ${retries})`);

      // Wait for the back-off delay
      await new Promise(resolve => setTimeout(resolve, backoffDelay));
    }
  }
}

export {
  initializeApi,
  authenticate,
  alreadySignedIn,
  labelEmail,
  getEmails,
  getEmail,
  listLabels,
  signout,
  getMessages,
  getBody,
  getHeaders,
  getSubjectLine,
  getThread,
  loadBatch,
  moreMessages,
  unLabelEmail,
  getOldestTimeQuery,
  getNewestTimeQuery,
  platformDate,
  getProfileEmailAddress,
  getEmailAddrs,
  getThreadSender,
  getThreadDate,
  getThreadSnippet,
  getThreadSubject
}
